import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";
import { App as CapacitorApp } from "@capacitor/app";
import { pingActivity } from "../../services/activityPing";

/**
 * Tells the backend the member is using the app (services/activityPing.js):
 * at sign-in, on every screen change and whenever the app comes back to the
 * foreground. The service keeps it to one ping every five minutes.
 *
 * Headless. It renders nothing and mounts once inside <IonReactRouter>, next
 * to AnalyticsScreenTracker.
 */
const ActivityPingTracker = () => {
  const { pathname } = useLocation();
  const userId = useSelector((state) => state.user?.user?.id);

  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  useEffect(() => {
    pingActivity(userId);
  }, [userId, pathname]);

  useEffect(() => {
    let disposed = false;
    let handle;

    const onVisible = () => {
      if (document.visibilityState === "visible") pingActivity(userIdRef.current);
    };
    document.addEventListener("visibilitychange", onVisible);

    // Native: the WebView's visibility doesn't always change on resume.
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (!disposed && isActive) pingActivity(userIdRef.current);
    })
      .then((listener) => {
        handle = listener;
        if (disposed) listener.remove();
      })
      .catch(() => {});

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisible);
      handle?.remove();
    };
  }, []);

  return null;
};

export default ActivityPingTracker;
